import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { LocaleLink } from "@/components/site/LocaleLink";
import { useState, useTransition } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { submitQuote } from "@/lib/quotes.functions";
import { trackEvent } from "@/lib/analytics";
import { CheckCircle, Upload, X, Loader2 } from "lucide-react";
import { FaqSection } from "@/components/site/FaqSection";
import { TrustBar } from "@/components/site/TrustBar";
import { Container } from "@/components/site/Container";
import { CinematicHero } from "@/components/site/CinematicHero";
import { Section } from "@/components/site/Section";
import homeHeroSmall from "@/assets/home-hero-640.avif";
import homeHero from "@/assets/home-hero.avif";
import { faqs, faqJsonLd } from "@/content/faqs";

import { business, canonical, breadcrumbJsonLd, shareImageMeta, langPageMeta, responseTime } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/offerte";
const SLEUTEL = "offerte";

const HERO_FALLBACK = { src: homeHero, srcSet: `${homeHeroSmall} 640w, ${homeHero} 1024w` };

const CODE_META_TITLE = "Gratis offerte of renovatie-diagnose zwembad";
const CODE_META_DESCRIPTION = `Vraag een gratis en vrijblijvende offerte of renovatie-diagnose aan voor uw zwembad in Limburg en Zuid-Nederland. Antwoord ${responseTime.within}.`;

export const Route = createFileRoute("/$lang/offerte")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: `Gratis offerte of renovatie-diagnose voor uw zwembad. Antwoord ${responseTime.within}.` },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("offerte", "Vraag een offerte aan voor uw zwembad bij Gopro Projects"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [faqJsonLd(faqs.offerte), breadcrumbJsonLd("Offerte aanvragen", PATH)],
    };
  },
  component: OffertePage,
});


const quoteSchema = z.object({
  type: z.enum(["nieuwbouw", "renovatie", "techniek", "warmtepomp", "overige"]),
  poolType: z.string().optional(),
  dimensions: z.string().max(255).optional(),
  age: z.string().max(255).optional(),
  location: z.string().max(255).optional(),
  description: z.string().max(2000).optional(),
  firstName: z.string().min(1, "Voornaam is verplicht").max(255),
  lastName: z.string().min(1, "Achternaam is verplicht").max(255),
  email: z.string().email("Geldig e-mailadres vereist").max(255),
  phone: z.string().max(255).optional(),
  address: z.string().max(500).optional(),
  preferredContact: z.enum(["email", "phone", "whatsapp"]).default("email"),
});

type FormState = z.infer<typeof quoteSchema>;

const initialState: FormState = {
  type: "nieuwbouw",
  poolType: undefined,
  dimensions: "",
  age: "",
  location: "",
  description: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  preferredContact: "email",
};

interface PhotoFile {
  file: File;
  preview: string;
}

function OffertePage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);
  const infoKaarten = c.kaarten("info", [
    {
      titel: "Wat gebeurt er daarna?",
      tekst: `Wij bekijken uw aanvraag en contacteren u ${responseTime.within} telefonisch of per e-mail met de eerste vragen en een voorstel voor een opname ter plaatse.`,
    },
    {
      titel: "Welke info helpt ons?",
      tekst:
        "Afmetingen van het bad, het type (bouwkundig, monoblock of liner), de leeftijd, het probleem dat u vaststelt en enkele foto's. Hoe concreter, hoe sneller wij u een juist antwoord geven.",
    },
    {
      titel: "Vrijblijvend en gratis",
      tekst:
        "Zowel de offerte als de renovatie-diagnose zijn gratis en zonder verplichting. Is renoveren niet zinvol, dan zeggen wij dat eerlijk.",
    },
  ]);
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isPending, startTransition] = useTransition();
  const submit = useServerFn(submitQuote);


  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePhotos = (files: FileList | null) => {
    if (!files) return;
    const newPhotos: PhotoFile[] = [];
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      if (photos.length + newPhotos.length >= 5) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setPhotos((prev) => [...prev, { file, preview }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = quoteSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      result.error.errors.forEach((err) => {
        const key = err.path[0] as keyof FormState;
        fieldErrors[key] = err.message;
      });
      setErrors(fieldErrors);
      toast.error("Controleer de gemarkeerde velden");
      return;
    }

    startTransition(async () => {
      try {
        await submit({
          data: {
            ...result.data,
            photos: photos.map((p) => ({
              name: p.file.name,
              type: p.file.type,
              dataUrl: p.preview,
            })),
          },
        });
        setSubmitted(true);
        trackEvent("generate_lead", { itemId: result.data.type, props: { service: result.data.type } });
        toast.success(`Uw aanvraag is verzonden. Wij contacteren u ${responseTime.within}.`);
      } catch (err) {
        toast.error("Er ging iets mis bij het verzenden. Probeer opnieuw.");
        console.error(err);
      }
    });
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-primary text-primary-foreground">
        <Container className="flex min-h-screen max-w-3xl flex-col justify-center py-32">
          <CheckCircle className="mb-6 h-12 w-12 text-secondary" strokeWidth={1.75} />
          <h1 className="display-section">{c.veld("bedankt", "titel", "Bedankt voor uw aanvraag")}</h1>
          <p className="mt-4 text-[17px] leading-7 text-primary-foreground/85">
            {c.rijkInline("bedankt",
              `We hebben uw gegevens ontvangen. Onze specialisten bekijken uw project en contacteren u ${responseTime.within}.`,
            )}
          </p>
          <Button asChild className="mt-8 bg-secondary text-secondary-foreground hover:bg-secondary/90">
            <LocaleLink to="/">{c.veld("bedankt", "knopTekst", "Terug naar home")}</LocaleLink>
          </Button>
        </Container>
      </main>
    );
  }

  return (
    <main>
      <CinematicHero
        overline={c.veld("hero", "overline", "Offerte")}
        title={c.veld("hero", "titel", "Offerte of renovatie diagnose")}
        subtitle={c.rijkInline("hero",
          `Vul het formulier in en ontvang ${responseTime.within} een vrijblijvende offerte of diagnose.`,
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={c.veld("hero", "alt", "Verlicht bouwkundig zwembad in een aangelegde tuin in Limburg")}
      >
            <p className="text-[17px] leading-7 text-primary-foreground/85">
              Liever meteen iemand spreken? Bel <a className="font-semibold text-secondary hover:underline" href={`tel:${business.phoneE164}`}>{business.phone}</a>.
            </p>
      </CinematicHero>
      <section className="bg-primary text-primary-foreground">
        <Container>
          <div className="mt-12 grid gap-px bg-primary-foreground/15 sm:grid-cols-3">
            {infoKaarten.map((kaart) => (
              <div key={kaart.titel} className="bg-primary p-6">
                <h2 className="font-display text-base font-semibold">{kaart.titel}</h2>
                <p className="mt-2 text-sm leading-6 text-primary-foreground/80">{kaart.tekst}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>


      <TrustBar />

      <Section tone="mist">
        <div className="mx-auto max-w-4xl">
          <div className="bg-background p-6 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-5 border-b border-border pb-10">
                <div className="flex items-baseline gap-4"><span className="font-display text-sm font-semibold text-brand-deep">01</span><Label className="text-base font-semibold">Waar gaat uw aanvraag over?</Label></div>
                <RadioGroup
                  value={form.type}
                  onValueChange={(v) => updateField("type", v as FormState["type"])}
                  className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {[
                    { value: "nieuwbouw", label: "Nieuwbouw zwembad" },
                    { value: "renovatie", label: "Renovatie" },
                    { value: "techniek", label: "Techniek / lekkage" },
                    { value: "warmtepomp", label: "Warmtepomp / afdekking" },
                    { value: "overige", label: "Overige vraag" },
                  ].map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem value={option.value} id={option.value} className="peer sr-only" />
                      <Label
                        htmlFor={option.value}
                        className="flex cursor-pointer items-center justify-center rounded border border-border bg-background p-4 text-sm font-medium transition-colors peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary peer-data-[state=checked]:text-primary-foreground"
                      >
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-6 border-b border-border pb-10">
                <div className="flex items-baseline gap-4"><span className="font-display text-sm font-semibold text-brand-deep">02</span><h2 className="font-display text-base font-semibold">Uw zwembad of project</h2></div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="poolType">Type zwembad</Label>
                  <Select value={form.poolType || ""} onValueChange={(v) => updateField("poolType", v || undefined)}>
                    <SelectTrigger id="poolType" className="data-[placeholder]:text-on-surface-variant">
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monoblock">Polyester monoblock</SelectItem>
                      <SelectItem value="bouwkundig">Bouwkundig bad</SelectItem>
                      <SelectItem value="liner">Liner / foliebad</SelectItem>
                      <SelectItem value="andere">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dimensions">Afmetingen (l x b x d)</Label>
                  <Input
                    id="dimensions"
                    value={form.dimensions}
                    onChange={(e) => updateField("dimensions", e.target.value)}
                    placeholder="bijv. 8m x 4m x 1,5m"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age">Leeftijd van het bad</Label>
                  <Input
                    id="age"
                    value={form.age}
                    onChange={(e) => updateField("age", e.target.value)}
                    placeholder="bijv. 15 jaar"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Plaats / regio</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => updateField("location", e.target.value)}
                    placeholder="bijv. Antwerpen"
                  />
                </div>
              </div></div>

              <div className="space-y-2">
                <Label htmlFor="description">Omschrijving van uw vraag</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Vertel kort wat u zoekt of welk probleem u ervaart..."
                  rows={5}
                />
              </div>

              <div className="space-y-4 border-b border-border pb-10">
                <div className="flex items-baseline gap-4"><span className="font-display text-sm font-semibold text-brand-deep">03</span><Label className="text-base font-semibold">Foto's toevoegen (optioneel)</Label></div>
                <div className="flex items-center gap-4">
                  <Label
                    htmlFor="photos"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-outline/30 bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                  >
                    <Upload className="h-4 w-4" />
                    Kies foto's
                  </Label>
                  <Input
                    id="photos"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handlePhotos(e.target.files)}
                  />
                  <span className="text-xs text-on-surface-variant">Max. 5 foto's</span>
                </div>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative aspect-square overflow-hidden border border-border">
                        <img
                          src={photo.preview}
                          alt={`Foto ${idx + 1}`}
                          width={160}
                          height={160}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute right-1 top-1 rounded-full bg-foreground/80 p-1 text-background hover:bg-foreground"
                          aria-label={`Foto ${idx + 1} verwijderen`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-6 border-b border-border pb-10">
                <div className="flex items-baseline gap-4"><span className="font-display text-sm font-semibold text-brand-deep">04</span><h2 className="font-display text-base font-semibold">Uw contactgegevens</h2></div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">Voornaam *</Label>
                  <Input
                    id="firstName"
                    value={form.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    className={errors.firstName ? "border-destructive" : ""}
                  />
                  {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Achternaam *</Label>
                  <Input
                    id="lastName"
                    value={form.lastName}
                    onChange={(e) => updateField("lastName", e.target.value)}
                    className={errors.lastName ? "border-destructive" : ""}
                  />
                  {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefoon</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="0484 97 64 77"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Adres van het project</Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                  />
                </div>
              </div></div>

              <div className="space-y-4">
                <div className="flex items-baseline gap-4"><span className="font-display text-sm font-semibold text-brand-deep">05</span><Label className="text-base font-semibold">Voorkeur voor contact</Label></div>
                <RadioGroup
                  value={form.preferredContact}
                  onValueChange={(v) => updateField("preferredContact", v as FormState["preferredContact"])}
                  className="flex flex-wrap gap-4"
                >
                  {[
                    { value: "email", label: "E-mail" },
                    { value: "phone", label: "Telefoon" },
                    { value: "whatsapp", label: "WhatsApp" },
                  ].map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <RadioGroupItem value={option.value} id={`contact-${option.value}`} />
                      <Label htmlFor={`contact-${option.value}`} className="cursor-pointer font-normal">
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <Button
                type="submit"
                disabled={isPending}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                size="lg"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  "Aanvraag verzenden"
                )}
              </Button>
            </form>
          </div>
        </div>
      </Section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.offerte} />
    </main>
  );
}
